import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import useMainstreamCareers from "../utils/useMainstreamCareers";
import EmergingAICareers from "../components/recommendCareers/EmergingAICareers";
import Loader from "../components/loader/Loader";
import Sidebar from "../components/Sidebar";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";

const MainstreamCareers = () => {
  const { currentJob } = useSelector((state) => state?.user);
  const userId = useSelector((state) => state.auth.id);
  const queryClient = useQueryClient();
  const [savedCareers, setSavedCareers] = useState([]);

  const {
    data: recommendData,
    isPending: recommendPending,
    error: recommendError,
  } = useMainstreamCareers(currentJob);

  useEffect(() => {
    if (currentJob) {
      // Invalidate all queries that depend on currentJob
      queryClient.invalidateQueries({
        queryKey: ["mainstreamCareers"],
        exact: false
      });
    }
  }, [currentJob, queryClient]);

  useEffect(() => {
    fetchSavedCareers();
  }, [userId]);

  const fetchSavedCareers = async () => {
    if (!userId) {
      return;
    }
    
    try {
      const response = await api1.get(`${API_ENDPOINTS.GET_CAREER_BOOKMARKS}/${userId}`);
      const savedCareersData = response.data.data || [];
      setSavedCareers(savedCareersData);
    } catch (error) {
      console.error("Error fetching saved careers:", error);
      setSavedCareers([]);
    }
  };

  const isCareerSaved = (career) => {
    if (!career || !savedCareers.length) {
      return false;
    }
    
    const isSaved = savedCareers.some(saved => {
      // Match by career name and type
      const nameMatch = saved.careerName === career.name || saved.title === career.name;
      const typeMatch = saved.careerType === "mainstream" || saved.category === "mainstream";
      
      return nameMatch && typeMatch;
    });
    
    return isSaved;
  };

  const mainstreamCareers = useMemo(() => {
    if (!recommendData) return [];
    const allCareers = [
      ...(recommendData.top_3_similar_occupations || []),
      ...(recommendData.top_3_advance_occupations || []),
      ...(recommendData.top_3_lateral_occupations || []),
    ];
    return allCareers;
  }, [recommendData]);

  if (recommendPending) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <Sidebar>
      <EmergingAICareers
        careers={mainstreamCareers}
        heading="Mainstream Careers"
        subtitle="Stable, established opportunities"
        isLoading={recommendPending}
        isEmerging={false}
        userId={userId}
        isCareerSaved={isCareerSaved}
        onBookmarkChange={fetchSavedCareers}
      />
    </Sidebar>
  );
};

export default MainstreamCareers;